import { useAuth0 } from '@auth0/auth0-react';
import { useQuery } from '@tanstack/react-query';

import { ENDPOINTS } from '@/constants/api';
import { QueryKeys } from '@/constants/queryKeys';
import { authTokenStorage } from '@/features/auth/external/authTokenStorage';
import { getApiClient } from '@/hooks/apiClient';
import { useAuthStore } from '@/store/authStore';

type QueryParams =
  | {
      platform: 'webex';
      platform_org_id: string;
      platform_meeting_id: string;
    }
  | {
      platform: 'teams';
      tenantId: string;
      userObjectId: string;
      meetingChatId: string;
    };

async function fetcher(params: QueryParams | null, token: string) {
  if (!params) {
    return null;
  }

  const apiClient = await getApiClient({ token });

  let url;

  switch (params.platform) {
    case 'webex': {
      url = `/platforms/webex/orgs/${params.platform_org_id}/meetings/${params.platform_meeting_id}`;

      try {
        const { data } = await apiClient.get<{ webLink: string }>(url, {
          _skipRedirect: true,
        });

        return data?.webLink ?? null;
      } catch (e) {
        console.error('Error fetching Webex meeting URL:', e);
        return null;
      }
    }
    case 'teams': {
      url = ENDPOINTS.msTeamsJoinMeeting(params.tenantId);

      try {
        const { data } = await apiClient.post<{ joinUrl: string }>(
          url,
          {
            userObjectId: params.userObjectId,
            meetingChatId: params.meetingChatId,
          },
          { _skipRedirect: true },
        );

        return data?.joinUrl ?? null;
      } catch (e) {
        console.error('Error fetching Teams meeting URL:', e);
        return null;
      }
    }
    default: {
      url = '';
    }
  }

  return null;
}

export function useGetMeetingUrl(params: QueryParams | null) {
  const { getAccessTokenSilently } = useAuth0();
  const isExternalAuth = useAuthStore((state) => state.isExternalAuth);

  return useQuery({
    queryKey: [QueryKeys.GET_MEETING_URL, params],
    queryFn: async () => {
      if (isExternalAuth) {
        const token = authTokenStorage.get();

        if (!token) {
          return null;
        }

        return fetcher(params, token);
      }

      const token = await getAccessTokenSilently();

      return fetcher(params, token);
    },
    enabled: !!params,
  });
}
